import { HTTPStatus, getInstance } from '@/api'
import type { TypedResponse } from '@/api/services/helpers'
import { getDeviceStatusFormattedResponse, getFormattedSensorData } from '@/store/devices/helper'
import type {
  DeviceModel,
  DeviceSummaryItem,
  DeviceModelField,
  ModelMetadata,
  StdTelemetry,
  StateSummary,
  DeviceActivations,
  IotCoreData,
  UserDeviceApiData
} from '@/types/devices'

export interface DeviceCoords {
  lat: number | null
  lng: number | null
}

export interface FormattedMeasurement {
  timestamp: string
  value: number | boolean | string | null
  type: string
}

export type SensorReadings = Record<string, FormattedMeasurement>

export interface UnpackedDevice {
  coords: DeviceCoords
  accountId: string
  deviceId: string | null
  deviceStatus: string[]
  model: string
  submodel: string
  serial: string
  indoor: boolean
  isPublic: boolean
  isShared: boolean
  name: string
  readings: SensorReadings
  sensorStatus: string[]
  telemetry: StdTelemetry
  timestamp: string | null
}

export interface UnpackedDeviceState {
  deviceId: string | null
  state: StateSummary | null
}

export interface UnpackedActivation {
  deviceId: string
  activation: DeviceActivations
}

export interface UnpackedIotCoreData {
  deviceId: string
  iotCoreData: IotCoreData
}

export interface UnpackDeviceResult {
  state: UnpackedDeviceState | undefined
  device: UnpackedDevice
  deviceId: string | null
  activation: UnpackedActivation | Record<string, never>
  iotCoreData: UnpackedIotCoreData | Record<string, never>
}

export interface UnpackDevicesResult {
  devices: Record<string, UnpackedDevice>
  state: UnpackedDeviceState[]
  activation: UnpackedActivation[]
  iotCoreData: UnpackedIotCoreData[]
}

export interface UnpackUserDevicesResult {
  devices: Record<string, UserDeviceApiData>
  activations: UnpackedActivation[]
}

export type UnpackedTelemetryField = Record<string, DeviceModelField>

export type UnpackedModelMetadata = Record<
  string,
  Omit<DeviceModel, 'telemetryFields'> & {
    telemetryFields: UnpackedTelemetryField
  }
>

export async function getAllDevices(includePublic = true): Promise<UnpackDevicesResult> {
  const apiService = getInstance()
  const devs = await apiService.getDevices(includePublic)
  return await unpackDevices(devs)
}

export async function unpackDevices(
  devs: TypedResponse<DeviceSummaryItem[]>
): Promise<UnpackDevicesResult> {
  if (devs.status === HTTPStatus.OK) {
    const body = await devs.json()
    const devices: Record<string, UnpackedDevice> = {}
    const devStates: UnpackedDeviceState[] = []
    const devActivations: UnpackedActivation[] = []
    const devIotCoreData: UnpackedIotCoreData[] = []

    for (const dev of body) {
      const { device, deviceId, state, activation, iotCoreData } = unpackDevice(dev)
      if (state) {
        devStates.push(state)
      }
      if (activation && 'deviceId' in activation) {
        devActivations.push(activation as UnpackedActivation)
      }
      if (iotCoreData && 'deviceId' in iotCoreData) {
        devIotCoreData.push(iotCoreData as UnpackedIotCoreData)
      }
      if (deviceId) {
        devices[deviceId] = device
      }
    }
    return {
      devices,
      state: devStates,
      activation: devActivations,
      iotCoreData: devIotCoreData
    }
  }

  throw new Error(`HTTP Request failed with status ${devs.status}`)
}

export function unpackDevice(dev: DeviceSummaryItem): UnpackDeviceResult {
  let state: UnpackedDeviceState | undefined
  let activation: UnpackedActivation | Record<string, never> = {}
  let iotCoreData: UnpackedIotCoreData | Record<string, never> = {}

  let deviceId: string | null = null

  const devDevice = dev.Device
  if (devDevice?.deviceId) {
    deviceId = devDevice.deviceId

    state = {
      deviceId,
      state: dev.State ?? null
    }
    if (dev.Activations) {
      activation = {
        deviceId,
        activation: dev.Activations
      }
    }
    if (dev.iot_core_data) {
      iotCoreData = {
        deviceId,
        iotCoreData: dev.iot_core_data
      }
    }
  }

  const telemetry = dev.Telemetry ?? null
  const deviceStatus = telemetry ? getDeviceStatusFormattedResponse(telemetry) : []
  const meta = dev.Device
  const attributes = dev.Attributes
  const coords: DeviceCoords = {
    lat: meta.latitude,
    lng: meta.longitude
  }
  const { measurements, sensorStatus } = getFormattedSensorData(dev?.Telemetry?.sensors)
  const deviceData: UnpackedDevice = {
    coords,
    accountId: meta.accountId,
    deviceId,
    deviceStatus,
    model: meta.model,
    submodel: attributes?.submodel as string,
    serial: meta.serial,
    indoor: meta.isIndoor,
    isPublic: meta.isPublic,
    isShared: meta.isShared,
    name: meta.friendlyName,
    readings: measurements,
    sensorStatus,
    telemetry,
    timestamp: telemetry?.timestamp ? telemetry.timestamp : null
  }

  return { state, device: deviceData, deviceId, activation, iotCoreData }
}

export async function getModelsMetaData(): Promise<UnpackedModelMetadata> {
  const apiService = getInstance()
  const response = await apiService.getModelsMetaData()
  if (response.status === HTTPStatus.OK) {
    const data = await response.json()
    return unpackModelsMetaData(data)
  }
  throw new Error(`HTTP Request failed with status ${response.status}`)
}

/**
 * Flattens and translates the telemetryFields array into objects for each model using reading name as the key.
 * @param metaData - Models metadata
 * @returns Object keyed by model with telemetryFields as a record
 */
export function unpackModelsMetaData(metaData: ModelMetadata): UnpackedModelMetadata {
  const metaDataObject = JSON.parse(JSON.stringify(metaData)) as UnpackedModelMetadata
  for (const model in metaDataObject) {
    const telemetryFields = metaData[model].telemetryFields
    if (telemetryFields) {
      metaDataObject[model].telemetryFields = telemetryFields.reduce(
        (allFields: UnpackedTelemetryField, currentField) => {
          allFields[currentField.name] = currentField
          return allFields
        },
        {}
      )
    } else {
      metaDataObject[model].telemetryFields = {}
    }
  }

  return metaDataObject
}

export async function getUserDevices(): Promise<UnpackUserDevicesResult> {
  const apiService = getInstance()
  const devs = await apiService.getUserDevices()
  return await unpackUserDevices(devs)
}

export async function unpackUserDevices(devs: Response): Promise<UnpackUserDevicesResult> {
  if (devs.status !== HTTPStatus.OK) {
    throw new Error(`HTTP Request failed with status ${devs.status}`)
  }

  const body = await devs.json()
  const devices: Record<string, UserDeviceApiData> = {}
  const activations: UnpackedActivation[] = []

  if (body) {
    for (const device of body as Array<Record<string, unknown>>) {
      if (device?.id) {
        devices[device.id as string] = device

        if (device.activations) {
          activations.push({
            deviceId: device.id as string,
            activation: device.activations
          })
        }
      }
    }
  }

  return { devices, activations }
}
